import { useMemo } from 'react';

import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import RotateIcon from '@cartwhl/ui/icons/RotateIcon';

import type { AxisTriplet } from '../../../lib/skeleton/boneTransforms';
import { getJointOffsetRotationForBoneRef } from '../../../lib/io/jointOffsets';
import type { MappingResult } from '../../../lib/recognize/config';
import ResetToDefaultButton from '../../../presentations/ResetToDefaultButton';
import { PANEL_WRITING_CLASSNAME } from '../../../presentations/panelWritingStyle';
import { INSPECTOR_ROTATE_DRAG_DELTA_PER_PIXEL } from '../constants';
import {
  ZERO_AXIS_TRIPLET,
  axisTripletDiffersFromBaseline,
} from './axisTripletFieldUtils';
import TransformAxisValuesRow from './TransformAxisValuesRow';

const JOINT_OFFSET_TOOLTIP =
  'Extra Euler rotation (degrees) stored in mapping joint_offsets under this bone ref. Applied as a post-process on retargeted motion and exported with Save / Export mapping.';

interface JointOffsetEditorProps {
  boneRef: string;
  mapping: MappingResult;
  compactLayout: boolean;
  onJointOffsetChange: (
    boneRef: string,
    rotation: [number, number, number] | null,
  ) => void;
  onHistoryAction?: (label: string, action: () => void) => void;
  onValueChangeStart?: () => void;
  onValueChangeEnd?: () => void;
  onDragValueChangeStart?: () => void;
}

export default function JointOffsetEditor({
  boneRef,
  mapping,
  compactLayout,
  onJointOffsetChange,
  onHistoryAction,
  onValueChangeStart,
  onValueChangeEnd,
  onDragValueChangeStart,
}: JointOffsetEditorProps) {
  const stored = useMemo(
    () => getJointOffsetRotationForBoneRef(mapping.ioExtras, boneRef),
    [boneRef, mapping.ioExtras],
  );
  const values: AxisTriplet = stored ?? ZERO_AXIS_TRIPLET;
  const isDefaultOffset = !axisTripletDiffersFromBaseline(
    values,
    ZERO_AXIS_TRIPLET,
    0,
  );

  const setAxis = (axisIndex: number, next: number) => {
    const nextRot = [...values] as AxisTriplet;
    nextRot[axisIndex] = next;
    onJointOffsetChange(
      boneRef,
      axisTripletDiffersFromBaseline(nextRot, ZERO_AXIS_TRIPLET, 0)
        ? nextRot
        : null,
    );
  };

  return (
    <div className="space-y-0.5">
      <div className="flex items-center justify-between gap-2 py-0.5">
        <Tooltip text={JOINT_OFFSET_TOOLTIP} side="top" inModal>
          <span className="min-w-0">
            <Text
              variant="studio/body/medium/xs"
              className={PANEL_WRITING_CLASSNAME}
            >
              Bone Offsets
            </Text>
          </span>
        </Tooltip>
        <ResetToDefaultButton
          label="joint offset"
          isDefault={isDefaultOffset}
          onReset={() =>
            onHistoryAction
              ? onHistoryAction('Reset bone offsets', () =>
                  onJointOffsetChange(boneRef, null),
                )
              : onJointOffsetChange(boneRef, null)
          }
        />
      </div>
      <Text
        variant="studio/body/medium/xs"
        className="text-secondary pr-10 leading-tight"
      >
        Applied after retargeting as a post-process correction.
      </Text>
      <TransformAxisValuesRow
        label="Rotate"
        labelIcon={<RotateIcon className="h-3 w-3" />}
        compact={compactLayout}
        values={values}
        editable
        ariaLabelPrefix="Bone offsets"
        onAxisChange={setAxis}
        baselineValues={ZERO_AXIS_TRIPLET}
        changeEpsilon={0}
        dragDeltaPerPixel={INSPECTOR_ROTATE_DRAG_DELTA_PER_PIXEL}
        onValueChangeStart={onValueChangeStart}
        onValueChangeEnd={onValueChangeEnd}
        onDragValueChangeStart={onDragValueChangeStart}
      />
    </div>
  );
}
